import {
  UseQueryOptions,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';

import { ShopModel, shopClient } from '@merchant-clients/shop';

import { useClientQuery } from '@frontend/hooks';
import { useOptionalUser } from '@frontend/utils';

const fetchShop = async (shopId: string) => {
  return shopClient.get(shopId);
};

export const getShopQueryKey = (shopId: string) => ['shop', shopId];

export interface UseShopsQueryProps {
  onError?(error: Error): void;
  shopIds: string[];
}

export interface UseShopQueryProps {
  shopId: string;
  onError?(error: Error): void;
}

export const useShopQuery = ({ shopId, onError }: UseShopQueryProps) => {
  return useClientQuery({
    queryKey: getShopQueryKey(shopId),
    enabled: !!shopId,
    async onFetch() {
      if (!shopId) {
        return Promise.reject(new Error('No shop ID provided.'));
      }

      return await fetchShop(shopId);
    },
    onError,
    refetchInterval: minutesToMilliseconds(15),
  });
};

export const useShopsQuery = ({ shopIds, onError }: UseShopsQueryProps) => {
  return useQueries({
    queries: shopIds.map(
      (shopId): UseQueryOptions<ShopModel> => ({
        queryKey: getShopQueryKey(shopId),
        queryFn: () => fetchShop(shopId),
        refetchInterval: minutesToMilliseconds(15),
      }),
      onError,
    ),
    combine: (results) => {
      const data = results.map((r) => r?.data)?.filter((d) => d !== undefined);
      const isLoading = results.some((r) => r.isLoading);
      const isError = results.some((r) => r.isError);
      const error = results.find((r) => r.isError)?.error;

      return {
        data,
        isLoading,
        isError,
        error,
      };
    },
  });
};

export const usePrefetchShops = () => {
  const { user } = useOptionalUser();
  const queryClient = useQueryClient();

  if (!user?.shops) {
    return;
  }

  for (const shopId of user.shops) {
    queryClient.prefetchQuery({
      queryKey: getShopQueryKey(shopId),
      queryFn: async () => {
        return await fetchShop(shopId);
      },
    });
  }
};
